import React from 'react';
import SectionHeader from '@components/common/SectionHeader';
import TargetPriceChart from '@components/charts/TargetPriceChart';
import ConsensusDonutChart, {
  type ConsensusDonutHoverSegment,
} from '@components/charts/ConsensusDonutChart';
import ConsensusTrendChart from '@components/charts/ConsensusTrendChart';
import { CHART_COLORS, CONSENSUS_PALETTE } from '@components/charts/chartConstants';
import { formatNumber, formatPercent, formatSigned, formatUpdateDate } from '@utils/format';
import type { AnalystForecast } from '@app-types/performance';

export interface AnalystForecastSectionProps {
  data: AnalystForecast;
}

const getConsensusSentimentColor = (
  label: string,
  segments: AnalystForecast['segments'],
): string => {
  const segmentIndex = segments.findIndex((segment) => segment.label === label);
  return segmentIndex >= 0
    ? CONSENSUS_PALETTE[segmentIndex % CONSENSUS_PALETTE.length]
    : CHART_COLORS.axisLabel;
};

/** Section 4: Analyst forecast (Figma 3144:5134 / mobile 3154:4457). */
const AnalystForecastSection: React.FC<AnalystForecastSectionProps> = ({ data }) => {
  const [hoveredConsensusSegment, setHoveredConsensusSegment] =
    React.useState<ConsensusDonutHoverSegment | null>(null);
  const { divergence } = data;
  const divergenceValue = divergence.value;
  const divergencePercent = divergence.percent;
  const hasDivergence = divergenceValue !== null && divergencePercent !== null;
  const divergenceToneClass = hasDivergence
    ? divergenceValue >= 0
      ? ' is-positive'
      : ' is-negative'
    : '';
  const divergenceText = `${formatSigned(divergenceValue, 2)} (${formatPercent(
    divergencePercent,
    2,
  )})`;
  const consensusSentiment = hoveredConsensusSegment ?? {
    label: data.sentiment,
    color: getConsensusSentimentColor(data.sentiment, data.segments),
  };

  return (
    <section className="summary-section analyst-forecast" data-node-id="3144:5134">
      <SectionHeader
        title="アナリスト予想"
        updateDate={`更新日：${formatUpdateDate(data.updateDate)}`}
        showHelp
      />

      <div className="analyst-forecast__target" data-node-id="3144:5218">
        <div className="analyst-forecast__target-intro" data-node-id="3333:13185">
          <div className="analyst-forecast__target-label-row">
            <p className="analyst-forecast__target-section-title">目標株価</p>
          </div>
          <div className="analyst-forecast__target-content">
            <div className="analyst-forecast__target-summary" data-node-id="3333:13141">
              <div className="analyst-forecast__target-card-row analyst-forecast__target-card-row--main">
                <span className="analyst-forecast__target-card-label">平均目標株価</span>
                <strong className="analyst-forecast__target-card-value">
                  {formatNumber(data.cardAverageDisplay, 2)}
                </strong>
              </div>
              <div className="analyst-forecast__target-card-row">
                <span className="analyst-forecast__target-card-sub-label">
                  現在値とのかい離 (率)
                </span>
                <span
                  className={`analyst-forecast__target-card-divergence${divergenceToneClass}`}
                >
                  {divergenceText}
                </span>
              </div>
            </div>
            <p className="analyst-forecast__target-description">
              {data.targetDescription}
            </p>
          </div>
        </div>

        <div
          className="analyst-forecast__target-chart-frame"
          data-node-id="3632:33710"
        >
          <TargetPriceChart
            targetPrice={data.targetPrice}
            ratingHistory={data.ratingHistory}
          />
        </div>

        {/*
          v20 refresh (D6): the old horizontal price row (Figma 3333:13042) is
          removed. High/average/low labels remain inside TargetPriceChart on the
          right side of the chart.
        */}

        <div className="analyst-forecast__consensus" data-node-id="3632:32603">
          <div className="analyst-forecast__consensus-part" data-node-id="3205:10052">
            <h3 className="analyst-forecast__consensus-title">コンセンサス</h3>
            <div
              className="analyst-forecast__sentiment"
              style={{ color: consensusSentiment.color }}
            >
              {consensusSentiment.label}
            </div>
            <div className="analyst-forecast__consensus-body">
              <div className="analyst-forecast__donut">
                <ConsensusDonutChart
                  segments={data.segments}
                  analystCount={data.analystCount}
                  sentiment={data.sentiment}
                  onSegmentHover={setHoveredConsensusSegment}
                />
              </div>
              <ul className="consensus-legend">
                {data.segments.map((seg, i) => (
                  <li key={seg.key} className="consensus-legend__item">
                    <span
                      className="consensus-legend__swatch"
                      style={{ backgroundColor: CONSENSUS_PALETTE[i % CONSENSUS_PALETTE.length] }}
                      aria-hidden="true"
                    />
                    <span className="consensus-legend__count">{formatNumber(seg.count, 0)}</span>
                    <span className="consensus-legend__label">{seg.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="analyst-forecast__consensus-part" data-node-id="3207:11343">
            <h3 className="analyst-forecast__consensus-title">コンセンサス推移</h3>
            <ConsensusTrendChart data={data.consensusTrend} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalystForecastSection;
